import React, {useState} from 'react';
import clsx from 'clsx';
import {
	FontAwesomeIcon,
	type FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';
import {
	Chip as MuiChip,
	chipClasses as muiChipClasses,
	type ChipProps as MuiChipProps,
} from '@mui/material';
import {
	ChipSkeleton,
	type ChipSkeletonProps,
} from '@netx/core-components/ChipSkeleton';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {
	composeClasses,
	styled,
	type WithClasses,
	type WithMuiPropOverrides,
} from '@netx/core-components/Theme';
import {Tooltip, type TooltipProps} from '@netx/core-components/Tooltip';
import {splitEventHandler} from '@netx/core-next/utils';
import {
	CHIP_CLASS_NAME,
	chipClasses,
	type ChipClasses,
	getChipUtilityClass,
} from './chipClasses';
import {ChipDeleteIcon, type ChipDeleteIconProps} from './ChipDeleteIcon';

export type ChipRef = React.ForwardedRef<HTMLDivElement>;

export type ChipUtilityProps = WithClasses<
	ChipClasses,
	{
		color?:
			| 'default'
			| 'primary'
			| 'secondary'
			| 'error'
			| 'info'
			| 'success'
			| 'warning';
		disabled?: boolean;
		meshAvatar?: boolean;
		variant?: MuiChipProps['variant'];
	}
>;

const useUtilityClasses = (props: ChipUtilityProps) => {
	const {classes} = props;
	const slots = {
		root: ['root'],
		deleteIcon: ['deleteIcon'],
		iconWrapper: ['iconWrapper'],
	};
	return composeClasses(slots, getChipUtilityClass, classes);
};

const ChipRoot = styled(MuiChip, {
	name: CHIP_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}) => {
	const {meshAvatar, color = 'default'} = ownerState as ChipUtilityProps;

	const palette = {
		...theme.palette,
		default: theme.palette.secondary,
	};

	return {
		...(color !== 'default'
			? {
					[`&.${muiChipClasses.outlined}`]: {
						borderColor: palette[color].main,
						[`& .${muiChipClasses.avatar}`]: {
							fontWeight: 600,
							// backgroundColor: palette[color].main,
							color: `#fff`,
						},
						[`& .${muiChipClasses.icon}`]: {
							color: `${palette[color].main}`,
						},
						[`& .${chipClasses.deleteIcon}`]: {
							color: `${palette[color].main}`,
						},
					},
			  }
			: {
					[`&.${muiChipClasses.outlined}`]: {
						[`& .${muiChipClasses.avatar}`]: {
							fontWeight: 600,
						},
					},
			  }),
		[`&:not(.${muiChipClasses.outlined})`]: {
			[`& .${muiChipClasses.avatar}`]: {
				// backgroundColor: `${palette[color].dark}`,// !important`,
				color: `#fff !important`,
			},
		},
		...(meshAvatar
			? {
					[`& .${muiChipClasses.avatar}`]: {
						marginLeft: 0,
						width: 32,
						height: 32,
					},
			  }
			: null),
	};
});

const ChipIconWrapper = styled('div', {
	name: CHIP_CLASS_NAME,
	slot: 'IconWrapper',
})(({theme}) => ({
	display: 'flex',
	padding: theme.spacing(0, 0, 0, 0.75),
}));

export interface ChipProps
	extends Omit<MuiChipProps, 'classes' | 'className' | 'icon' | 'sx'>,
		WithClasses<ChipClasses> {
	/**
	 * Is the delete button tabbable?
	 */
	deleteTabbable?: boolean;
	/**
	 * The title of the delete button
	 */
	deleteTitle?: string;
	/**
	 * Disable the tooltip for the chip
	 */
	disableTooltip?: boolean;
	/**
	 * The icon to display
	 */
	icon?: FontAwesomeIconProps['icon'] | null;
	/**
	 * Is the chip currently loading?
	 */
	loading?: boolean;
	/**
	 * Should the avatar be be mesh with the chip border
	 */
	meshAvatar?: boolean;
	/**
	 * The title of the chip
	 */
	title?: string;
	DeleteIconProps?: Partial<ChipDeleteIconProps>;
	SkeletonProps?: Partial<ChipSkeletonProps>;
	TooltipProps?: Partial<TooltipProps>;
}

export const ChipPrimitive = (
	{
		classes: _classes,
		className,
		color,
		deleteTabbable,
		deleteTitle,
		disabled = false,
		disableTooltip,
		icon,
		loading,
		meshAvatar = false,
		variant,
		title,
		onBlur,
		onFocus,
		DeleteIconProps,
		SkeletonProps,
		TooltipProps,
		...props
	}: ChipProps,
	ref: ChipRef,
) => {
	const [focused, setFocused] = useState(false);

	const ownerState: ChipUtilityProps = {
		classes: _classes,
		className,
		color,
		disabled,
		meshAvatar,
		variant,
	};

	const classes = useUtilityClasses(ownerState);

	const [_disableTitle, setDisableTitle] = useState(false);

	const handleBlur = useConstCallback(
		splitEventHandler(onBlur, () => setFocused(false)),
	);

	const handleFocus = useConstCallback(
		splitEventHandler(onFocus, () => setFocused(true)),
	);

	if (loading) return <ChipSkeleton {...SkeletonProps} />;

	return (
		<Tooltip
			disabled={disabled || disableTooltip}
			title={title ?? ''}
			{...TooltipProps}
			slotProps={{
				popper: {
					style: {
						display: _disableTitle ? 'none' : undefined,
					},
				},
			}}
		>
			<ChipRoot
				deleteIcon={
					<ChipDeleteIcon
						className={classes.deleteIcon}
						onMouseEnter={(event) => {
							event.stopPropagation();
						}}
						focused={focused}
						title={deleteTitle}
						tabbable={deleteTabbable}
						onTooltipToggle={setDisableTitle}
						{...DeleteIconProps}
					/>
				}
				{...props}
				ref={ref}
				className={clsx(classes.root, className)}
				color={color}
				disabled={disabled}
				icon={
					icon ? (
						<ChipIconWrapper className={classes.iconWrapper}>
							<FontAwesomeIcon icon={icon} fixedWidth />
						</ChipIconWrapper>
					) : undefined
				}
				ownerState={ownerState}
				variant={variant}
				onBlur={handleBlur}
				onFocus={handleFocus}
			/>
		</Tooltip>
	);
};

export const Chip = React.memo(React.forwardRef(ChipPrimitive)) as (
	props: ChipProps,
) => JSX.Element;

export default Chip;
